import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilder from '@/components/company/FormBuilder';
import type { FormField } from '@/components/company/FormBuilder';
import GenerateAIModal from '@/components/company/GenerateAIModal';
import AIResultModal from '@/components/company/AIResultModal';
import CultureAIGenerateForm from './culture/CultureAIGenerateForm';
import { cultureAIGenerateSchema, type CultureAIGenerateFormData } from './culture/culture-ai-generate.schema';
import { useGenerateCulture } from './culture/useGenerateCulture';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';

export default function Culture() {
  const formControl = useForm({
    defaultValues: {
      culture: '',
      is_published_culture: false,
    },
  });

  const { handleSubmit, reset, watch, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const { companyProfile, updateCompanyField, isLoading } = useCompanyState();
  const { currentTenant } = useTenantState();

  const isPublished = watch('is_published_culture');

  // AI Generate Form
  const aiFormControl = useForm<CultureAIGenerateFormData>({
    resolver: zodResolver(cultureAIGenerateSchema),
    defaultValues: {
      desired_values: '',
      work_environment: '',
      team_characteristics: '',
      leadership_style: '',
    },
  });

  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<string[]>([]);

  const generateCultureMutation = useGenerateCulture({
    tenantId: currentTenant?.id ? String(currentTenant.id) : '',
  });

  useEffect(() => {
    if (!companyProfile) return;

    const cultureValue =
      typeof companyProfile.culture === 'object' ? companyProfile.culture?.value : companyProfile.culture;

    const publishedValue =
      typeof companyProfile.culture === 'object'
        ? companyProfile.culture?.is_published
        : companyProfile.is_published_culture;

    reset({
      culture: cultureValue ?? '',
      is_published_culture: !!publishedValue,
    });
  }, [companyProfile, reset]);

  const handleSave = async (data: { culture: string; is_published_culture: boolean }) => {
    if (!currentTenant?.id) return;
    updateCompanyField(currentTenant.id, 'culture', data.culture, data.is_published_culture);
  };

  const handleGenerate = async (data: CultureAIGenerateFormData) => {
    try {
      const result = await generateCultureMutation.mutateAsync(data);

      if (result.success && result.data?.generated_options) {
        setGeneratedOptions(result.data.generated_options);
        setIsGenerateModalOpen(false);
        setIsResultModalOpen(true);
        toast.success('Culture generated successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate culture');
    }
  };

  const handleOpenGenerateModal = () => {
    aiFormControl.reset();
    setIsGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
  };

  const handleSaveGeneratedCulture = (selectedOption: string) => {
    setValue('culture', selectedOption);
    setIsResultModalOpen(false);
  };

  const handleAfterSave = async () => {
    if (!currentTenant?.id) return;
    const currentCulture = watch('culture');
    await updateCompanyField(currentTenant.id, 'culture', currentCulture, watch('is_published_culture'));
    toast.success('Culture saved to profile successfully');
  };

  const fields: FormField[] = [
    {
      name: 'culture',
      label: 'Company Culture & Values',
      type: 'richtext',
      required: true,
      placeholder: "Describe your company's culture, values, and practices...",
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title="Company Culture & Values"
        description="Define your company's core values, beliefs, and cultural practices."
        onGenerate={handleOpenGenerateModal}
        onSave={() => formRef.current?.requestSubmit()}
        isSaving={isLoading}
      />

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="pt-5">
          <FormBuilder
            fields={fields}
            initialPublished={isPublished}
            onPublishChange={val => formControl.setValue('is_published_culture', val)}
          />
        </form>
      </FormProvider>

      {/* Generate AI Modal */}
      <GenerateAIModal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        onSubmit={aiFormControl.handleSubmit(handleGenerate)}
        title="Generate Culture with AI"
        description="Provide information about your desired company culture to help AI generate comprehensive culture descriptions."
        generateButtonLabel="Generate Culture"
        isLoading={generateCultureMutation.isLoading}
      >
        <CultureAIGenerateForm
          control={aiFormControl.control}
          errors={aiFormControl.formState.errors}
          isLoading={generateCultureMutation.isLoading}
        />
      </GenerateAIModal>

      {/* AI Result Modal */}
      <AIResultModal
        isOpen={isResultModalOpen}
        onClose={handleCloseResultModal}
        options={generatedOptions}
        onSave={handleSaveGeneratedCulture}
        onAfterSave={handleAfterSave}
        title="AI Generated Culture"
        description="Select the culture description that best represents your company"
        saveButtonLabel="Save to Profile"
        isLoading={isLoading}
      />
    </section>
  );
}
